// ─────────────────────────────────────────────────────────────────────────────
// SurvAIHealth Metrics — Grievance Log mock seed data (v1, demo)
//
// ~28 seeded grievance records across the 6 pilot facilities, plus a 7-record
// block for the St. Jude (999012) demo facility, conforming to the shared shape
// in grievanceModel.js. This is the ONLY source of grievance data in v1. When
// the real data-relay feed lands, grievanceAdapter.js swaps its source and this
// file is no longer referenced — no report changes.
//
// Demo intent baked into the seed:
//   • Birch Creek (505289) is weighted heaviest.
//   • Eleanor Whitfield appears on 3 Birch Creek records (canon demo resident).
//   • A handful of resolved/closed records have residentNotificationSent = null
//     so the Dashboard "Notification gaps" tile lights up — the F585 money shot.
//   • days-to-close is varied so the distribution report is not flat.
//   • All categories / departments / statuses are represented.
// ─────────────────────────────────────────────────────────────────────────────

export const GRIEVANCE_MOCK_RECORDS = [
  // ── Birch Creek 505289 (heaviest — 9 records) ──────────────────────────────
  {
    id: "GRV-1001",
    facilityCcn: "505289",
    facilityName: "Birch Creek",
    complainantType: "family",
    residentName: "Eleanor Whitfield",
    dateReceived: "2026-01-12",
    category: "care",
    department: "nursing",
    assignedTo: "Maria Delgado, RN",
    status: "resolved",
    investigationNotes:
      "Daughter reported call light response times exceeding 20 minutes on the evening shift. Reviewed call light logs for the prior two weeks and interviewed three evening CNAs. Identified an unfilled aide assignment on the 200 hall.",
    resolutionDate: "2026-01-16",
    outcome:
      "Evening staffing matrix adjusted; a float CNA was assigned to the 200 hall. Response-time auditing added to the nurse manager's daily round.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-01-16",
    employeesMentioned: ["Maria Delgado", "Tonya Reeves"],
    narrative:
      "Family states resident Eleanor Whitfield waited over twenty minutes for assistance to the bathroom on multiple evenings, leading to an episode of incontinence and distress. Requests faster call light response.",
  },
  {
    id: "GRV-1002",
    facilityCcn: "505289",
    facilityName: "Birch Creek",
    complainantType: "resident",
    residentName: "Eleanor Whitfield",
    dateReceived: "2026-03-04",
    category: "dietary",
    department: "dietary",
    assignedTo: "Karen Whitfield, NHA",
    status: "closed",
    investigationNotes:
      "Resident reports repeated cold breakfast trays. Dietary tray-line temperatures spot-checked over five days; two readings were below threshold at the far end of the 200 hall.",
    resolutionDate: "2026-03-13",
    outcome:
      "Insulated tray covers reissued; tray-line-to-room delivery sequence reordered so the 200 hall is served first on alternating days.",
    residentNotificationSent: null, // RESERVED gap — feed lacked notification flag
    residentNotificationDate: null,
    employeesMentioned: ["Greg Patterson"],
    narrative:
      "Resident Eleanor Whitfield states her breakfast arrives cold most mornings and the coffee is lukewarm. She would like hot meals served at a proper temperature.",
  },
  {
    id: "GRV-1003",
    facilityCcn: "505289",
    facilityName: "Birch Creek",
    complainantType: "representative",
    residentName: "Eleanor Whitfield",
    dateReceived: "2026-05-28",
    category: "rights",
    department: "social_services",
    assignedTo: "Karen Whitfield, NHA",
    status: "in_progress",
    investigationNotes:
      "Power of attorney alleges resident's personal laundry repeatedly goes missing and that a roommate change was made without notice. Laundry tracking review underway; roommate-change notification log being pulled.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Denise Holloway"],
    narrative:
      "Representative for Eleanor Whitfield reports lost personal clothing and a room reassignment that the family was not informed of in advance. Concerned about dignity and communication.",
  },
  {
    id: "GRV-1004",
    facilityCcn: "505289",
    facilityName: "Birch Creek",
    complainantType: "family",
    residentName: "Harold Simmons",
    dateReceived: "2026-06-09",
    category: "staff_conduct",
    department: "nursing",
    assignedTo: "Maria Delgado, RN",
    status: "open",
    investigationNotes:
      "Son alleges a night-shift aide spoke harshly to his father during a transfer. Incident reported to Administrator; witness statements being collected and the named aide reassigned pending review.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Brandon Cole"],
    narrative:
      "Family member reports that an aide was rude and dismissive toward resident Harold Simmons during a late-night transfer, raising his voice. Requests staff coaching and an apology.",
  },
  {
    id: "GRV-1005",
    facilityCcn: "505289",
    facilityName: "Birch Creek",
    complainantType: "resident",
    residentName: "Doris Vance",
    dateReceived: "2026-02-18",
    category: "environment",
    department: "housekeeping",
    assignedTo: "Karen Whitfield, NHA",
    status: "closed",
    investigationNotes:
      "Resident reported a persistent odor and a slow bathroom drain. Maintenance work order placed; drain cleared and room deep-cleaned. Verified with resident.",
    resolutionDate: "2026-02-21",
    outcome: "Drain repaired, room deep-cleaned, and added to the weekly preventive plumbing check.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-02-21",
    employeesMentioned: ["Luis Ramos"],
    narrative:
      "Resident Doris Vance complains of an unpleasant smell in her bathroom and water draining slowly from the sink. Requests it be fixed and the room cleaned.",
  },
  {
    id: "GRV-1006",
    facilityCcn: "505289",
    facilityName: "Birch Creek",
    complainantType: "anonymous",
    residentName: null,
    dateReceived: "2026-04-22",
    category: "staff_conduct",
    department: "administration",
    assignedTo: "Karen Whitfield, NHA",
    status: "resolved",
    investigationNotes:
      "Anonymous note in the suggestion box alleges favoritism in activity sign-ups. Reviewed activity rosters; no pattern of exclusion found but sign-up process was opaque.",
    resolutionDate: "2026-05-06",
    outcome: "Activity sign-up moved to a posted first-come sheet with staff oversight; communicated at resident council.",
    residentNotificationSent: null, // RESERVED gap — anonymous, no resident to notify recorded
    residentNotificationDate: null,
    employeesMentioned: ["Priya Nair"],
    narrative:
      "Anonymous complaint that certain residents are always chosen for the best outings while others are passed over. Asks for a fair sign-up process.",
  },
  {
    id: "GRV-1007",
    facilityCcn: "505289",
    facilityName: "Birch Creek",
    complainantType: "family",
    residentName: "Walter Nguyen",
    dateReceived: "2026-06-13",
    category: "care",
    department: "therapy",
    assignedTo: "Maria Delgado, RN",
    status: "open",
    investigationNotes:
      "Daughter reports physical therapy sessions were missed three days in a row. Therapy schedule and attendance logs being reviewed against the treatment plan.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Sandra Kim"],
    narrative:
      "Family states resident Walter Nguyen's scheduled physical therapy did not occur for three consecutive days, slowing his recovery after a hip procedure.",
  },
  {
    id: "GRV-1008",
    facilityCcn: "505289",
    facilityName: "Birch Creek",
    complainantType: "resident",
    residentName: "Frances Abbott",
    dateReceived: "2025-12-29",
    category: "billing",
    department: "administration",
    assignedTo: "Karen Whitfield, NHA",
    status: "closed",
    investigationNotes:
      "Resident disputes a personal-needs-account charge for items she says she did not order. Business office reconciled the ledger and located a posting error.",
    resolutionDate: "2026-01-09",
    outcome: "Erroneous charge reversed and account credited; itemized statement provided.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-01-09",
    employeesMentioned: ["Janet Brooks"],
    narrative:
      "Resident Frances Abbott questions charges on her personal needs account for toiletries she did not request and asks for a refund and an explanation.",
  },
  {
    id: "GRV-1009",
    facilityCcn: "505289",
    facilityName: "Birch Creek",
    complainantType: "family",
    residentName: "Robert Esparza",
    dateReceived: "2026-05-11",
    category: "care",
    department: "nursing",
    assignedTo: "Maria Delgado, RN",
    status: "resolved",
    investigationNotes:
      "Wife reports medication was administered late on two occasions. MAR audited; two late entries confirmed during a shift-change gap. Charge nurse re-educated on hand-off.",
    resolutionDate: "2026-05-15",
    outcome: "Shift-change medication pass tightened; second-nurse verification added during hand-off window.",
    residentNotificationSent: null, // RESERVED gap
    residentNotificationDate: null,
    employeesMentioned: ["Tonya Reeves", "Maria Delgado"],
    narrative:
      "Family of resident Robert Esparza reports his evening blood pressure medication was given more than an hour late twice this week and wants assurance it will be on time.",
  },

  // ── Bluestone 515186 (5 records) ───────────────────────────────────────────
  {
    id: "GRV-2001",
    facilityCcn: "515186",
    facilityName: "Bluestone",
    complainantType: "resident",
    residentName: "Mabel Carter",
    dateReceived: "2026-02-02",
    category: "environment",
    department: "housekeeping",
    assignedTo: "Derek Olsen, NHA",
    status: "closed",
    investigationNotes:
      "Resident reports the room was not cleaned over a weekend. Housekeeping coverage gap confirmed for that weekend; supervisor rotation adjusted.",
    resolutionDate: "2026-02-07",
    outcome: "Weekend housekeeping coverage added; supervisor verifies room rounds each weekend.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-02-07",
    employeesMentioned: ["Carla Jensen"],
    narrative:
      "Resident Mabel Carter states her room went uncleaned over the weekend, with overflowing trash and an unmade bed. Requests reliable weekend service.",
  },
  {
    id: "GRV-2002",
    facilityCcn: "515186",
    facilityName: "Bluestone",
    complainantType: "family",
    residentName: "George Lindqvist",
    dateReceived: "2026-03-19",
    category: "care",
    department: "nursing",
    assignedTo: "Derek Olsen, NHA",
    status: "resolved",
    investigationNotes:
      "Son reports a pressure-area concern not acted on promptly. Skin assessment records reviewed; weekly skin check had lapsed. Wound nurse re-evaluated resident.",
    resolutionDate: "2026-03-31",
    outcome: "Weekly skin rounds re-instituted with WOCN sign-off; resident's care plan updated with repositioning schedule.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-03-31",
    employeesMentioned: ["Angela Pruitt"],
    narrative:
      "Family of resident George Lindqvist is concerned that a reddened area on his heel was not addressed quickly enough and wants closer skin monitoring.",
  },
  {
    id: "GRV-2003",
    facilityCcn: "515186",
    facilityName: "Bluestone",
    complainantType: "representative",
    residentName: "Yolanda Pierce",
    dateReceived: "2026-06-02",
    category: "rights",
    department: "social_services",
    assignedTo: "Derek Olsen, NHA",
    status: "in_progress",
    investigationNotes:
      "Guardian alleges resident's mail was opened before delivery. Mail-handling procedure under review; staff interviews scheduled.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Marcus Webb"],
    narrative:
      "Representative for resident Yolanda Pierce reports her mail appears to have been opened prior to being handed to her, raising a privacy and rights concern.",
  },
  {
    id: "GRV-2004",
    facilityCcn: "515186",
    facilityName: "Bluestone",
    complainantType: "resident",
    residentName: "Stanley Koch",
    dateReceived: "2026-06-15",
    category: "dietary",
    department: "dietary",
    assignedTo: "Derek Olsen, NHA",
    status: "open",
    investigationNotes:
      "Resident states his diabetic diet order is not being followed at supper. Comparing tray tickets against the physician diet order.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Nadia Foster"],
    narrative:
      "Resident Stanley Koch reports being served regular desserts despite a physician-ordered diabetic diet and is worried about his blood sugar.",
  },
  {
    id: "GRV-2005",
    facilityCcn: "515186",
    facilityName: "Bluestone",
    complainantType: "family",
    residentName: "Irene Sokolov",
    dateReceived: "2026-01-27",
    category: "other",
    department: "administration",
    assignedTo: "Derek Olsen, NHA",
    status: "closed",
    investigationNotes:
      "Daughter reports difficulty reaching the social worker by phone. Call-back logging reviewed; messages were not consistently routed.",
    resolutionDate: "2026-02-04",
    outcome: "Central message log implemented with a 24-hour call-back standard; family given a direct extension.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-02-04",
    employeesMentioned: ["Marcus Webb"],
    narrative:
      "Family of resident Irene Sokolov says repeated phone messages to the social services office went unreturned for over a week.",
  },

  // ── Woodard Creek 505387 (4 records) ───────────────────────────────────────
  {
    id: "GRV-3001",
    facilityCcn: "505387",
    facilityName: "Woodard Creek",
    complainantType: "resident",
    residentName: "Clyde Bannister",
    dateReceived: "2026-04-08",
    category: "care",
    department: "nursing",
    assignedTo: "Renee Tucker, RN",
    status: "resolved",
    investigationNotes:
      "Resident reports his incontinence brief was not changed timely overnight. Night-shift rounding documentation reviewed; a missed round confirmed.",
    resolutionDate: "2026-04-12",
    outcome: "Night rounding checklist added with hourly initials; nurse manager spot-audits twice weekly.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-04-12",
    employeesMentioned: ["Hannah Boyd"],
    narrative:
      "Resident Clyde Bannister states he was left in a wet brief for hours overnight and asks for more attentive overnight care.",
  },
  {
    id: "GRV-3002",
    facilityCcn: "505387",
    facilityName: "Woodard Creek",
    complainantType: "family",
    residentName: "Lucille Hartman",
    dateReceived: "2026-05-20",
    category: "staff_conduct",
    department: "therapy",
    assignedTo: "Renee Tucker, RN",
    status: "in_progress",
    investigationNotes:
      "Daughter alleges a therapy aide was dismissive of resident's pain reports. Therapy notes reviewed; pain-scale documentation inconsistent. Interviewing staff.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Eric Salazar"],
    narrative:
      "Family of resident Lucille Hartman reports a therapy aide ignored her complaints of shoulder pain during exercises and pushed her to continue.",
  },
  {
    id: "GRV-3003",
    facilityCcn: "505387",
    facilityName: "Woodard Creek",
    complainantType: "resident",
    residentName: "Otis Freeman",
    dateReceived: "2026-06-11",
    category: "environment",
    department: "housekeeping",
    assignedTo: "Renee Tucker, RN",
    status: "open",
    investigationNotes:
      "Resident reports his window will not close and the room is cold at night. Maintenance work order submitted; awaiting part.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: [],
    narrative:
      "Resident Otis Freeman states his window is stuck open and his room is uncomfortably cold overnight. Requests it be repaired promptly.",
  },
  {
    id: "GRV-3004",
    facilityCcn: "505387",
    facilityName: "Woodard Creek",
    complainantType: "representative",
    residentName: "Beatrice Cho",
    dateReceived: "2025-12-15",
    category: "billing",
    department: "administration",
    assignedTo: "Renee Tucker, RN",
    status: "closed",
    investigationNotes:
      "POA disputes a late-fee assessment. Statement history reviewed; payment was posted to the wrong account.",
    resolutionDate: "2026-01-02",
    outcome: "Late fee waived and payment reposted; statement corrected and explained to representative.",
    residentNotificationSent: null, // RESERVED gap
    residentNotificationDate: null,
    employeesMentioned: ["Olivia Grant"],
    narrative:
      "Representative for resident Beatrice Cho disputes a late fee, stating payment was made on time, and requests the fee be removed.",
  },

  // ── TLC Care Center 295071 (4 records) ─────────────────────────────────────
  {
    id: "GRV-4001",
    facilityCcn: "295071",
    facilityName: "TLC Care Center",
    complainantType: "family",
    residentName: "Theodore Marsh",
    dateReceived: "2026-03-26",
    category: "care",
    department: "nursing",
    assignedTo: "Gloria Mendez, NHA",
    status: "resolved",
    investigationNotes:
      "Wife reports a fall was not communicated to her the same day. Incident report timeline reviewed; family notification was delayed past policy.",
    resolutionDate: "2026-03-29",
    outcome: "Post-fall family-notification step added to the incident workflow with a same-day call requirement.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-03-29",
    employeesMentioned: ["Patrick Doyle"],
    narrative:
      "Family of resident Theodore Marsh reports learning of his fall a full day later and asks to be notified immediately of any incident.",
  },
  {
    id: "GRV-4002",
    facilityCcn: "295071",
    facilityName: "TLC Care Center",
    complainantType: "resident",
    residentName: "Pearl Donovan",
    dateReceived: "2026-05-04",
    category: "dietary",
    department: "dietary",
    assignedTo: "Gloria Mendez, NHA",
    status: "closed",
    investigationNotes:
      "Resident reports limited choices on the puréed menu. Menu cycle reviewed with the dietitian; variety options expanded.",
    resolutionDate: "2026-05-12",
    outcome: "Puréed menu expanded with additional entrées and a preference interview completed with the resident.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-05-12",
    employeesMentioned: ["Rachel Stein"],
    narrative:
      "Resident Pearl Donovan states the puréed meals are repetitive and bland and asks for more variety and seasoning.",
  },
  {
    id: "GRV-4003",
    facilityCcn: "295071",
    facilityName: "TLC Care Center",
    complainantType: "anonymous",
    residentName: null,
    dateReceived: "2026-06-07",
    category: "staff_conduct",
    department: "nursing",
    assignedTo: "Gloria Mendez, NHA",
    status: "in_progress",
    investigationNotes:
      "Anonymous complaint alleges staff using phones at the nurse station instead of answering call lights. Reviewing call-light response data and observing the unit.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: [],
    narrative:
      "Anonymous complaint that nursing staff are on personal phones at the desk while call lights go unanswered, especially in the afternoon.",
  },
  {
    id: "GRV-4004",
    facilityCcn: "295071",
    facilityName: "TLC Care Center",
    complainantType: "family",
    residentName: "Nathaniel Boone",
    dateReceived: "2026-01-19",
    category: "rights",
    department: "social_services",
    assignedTo: "Gloria Mendez, NHA",
    status: "closed",
    investigationNotes:
      "Son alleges resident was not included in his own care-plan meeting. Care-conference invitations reviewed; scheduling did not accommodate the resident.",
    resolutionDate: "2026-02-06",
    outcome: "Care-conference scheduling revised to ensure resident and family inclusion; written notice provided in advance.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-02-06",
    employeesMentioned: ["Sofia Reyes"],
    narrative:
      "Family of resident Nathaniel Boone reports the care-plan meeting was held without inviting the resident, who wished to participate in decisions about his care.",
  },

  // ── Ormond Nursing 195221 (3 records) ──────────────────────────────────────
  {
    id: "GRV-5001",
    facilityCcn: "195221",
    facilityName: "Ormond Nursing",
    complainantType: "resident",
    residentName: "Vernon Pratt",
    dateReceived: "2026-04-30",
    category: "environment",
    department: "housekeeping",
    assignedTo: "Howard Quinn, NHA",
    status: "resolved",
    investigationNotes:
      "Resident reports a broken recliner and a flickering light. Maintenance tickets verified and completed; resident confirmed satisfaction.",
    resolutionDate: "2026-05-03",
    outcome: "Recliner replaced and light fixture repaired; both added to the preventive maintenance log.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-05-03",
    employeesMentioned: ["Dale Norris"],
    narrative:
      "Resident Vernon Pratt reports his recliner is broken and a ceiling light flickers, making the room uncomfortable. Requests repairs.",
  },
  {
    id: "GRV-5002",
    facilityCcn: "195221",
    facilityName: "Ormond Nursing",
    complainantType: "family",
    residentName: "Gladys Ferraro",
    dateReceived: "2026-06-05",
    category: "care",
    department: "nursing",
    assignedTo: "Howard Quinn, NHA",
    status: "open",
    investigationNotes:
      "Daughter reports mother's hearing aids have gone missing twice. Personal-property log and room searches initiated.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Tammy Lowe"],
    narrative:
      "Family of resident Gladys Ferraro reports her hearing aids keep disappearing and worry about replacement cost and her ability to communicate.",
  },
  {
    id: "GRV-5003",
    facilityCcn: "195221",
    facilityName: "Ormond Nursing",
    complainantType: "representative",
    residentName: "Hugh Castellano",
    dateReceived: "2026-02-25",
    category: "billing",
    department: "administration",
    assignedTo: "Howard Quinn, NHA",
    status: "closed",
    investigationNotes:
      "Guardian requests an itemized bill they had not received. Business office produced the itemization and walked through the charges.",
    resolutionDate: "2026-03-21",
    outcome: "Itemized statement provided and a recurring monthly statement set up for the representative.",
    residentNotificationSent: null, // RESERVED gap
    residentNotificationDate: null,
    employeesMentioned: ["Bianca Flores"],
    narrative:
      "Representative for resident Hugh Castellano asks for a detailed, itemized bill, stating prior statements were vague and hard to follow.",
  },

  // ── Monroe Health 445457 (3 records) ───────────────────────────────────────
  {
    id: "GRV-6001",
    facilityCcn: "445457",
    facilityName: "Monroe Health",
    complainantType: "resident",
    residentName: "Estelle Park",
    dateReceived: "2026-03-11",
    category: "care",
    department: "nursing",
    assignedTo: "Felicia Barnes, NHA",
    status: "resolved",
    investigationNotes:
      "Resident reports pain medication offered inconsistently. MAR and PRN documentation reviewed; PRN offers not consistently charted.",
    resolutionDate: "2026-03-24",
    outcome: "PRN pain protocol re-educated to nursing; scheduled pain reassessment added to the eMAR.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-03-24",
    employeesMentioned: ["Vincent Hale"],
    narrative:
      "Resident Estelle Park reports her pain medication is sometimes offered and sometimes not, and she is uncertain when she can ask for it.",
  },
  {
    id: "GRV-6002",
    facilityCcn: "445457",
    facilityName: "Monroe Health",
    complainantType: "family",
    residentName: "Curtis Mbeki",
    dateReceived: "2026-06-14",
    category: "staff_conduct",
    department: "administration",
    assignedTo: "Felicia Barnes, NHA",
    status: "open",
    investigationNotes:
      "Son reports front-desk staff were curt when he visited after hours. Reviewing visitor-policy communication and reception coverage.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Monica Yates"],
    narrative:
      "Family of resident Curtis Mbeki reports being treated rudely by reception during an after-hours visit and being given conflicting information about visiting hours.",
  },
  {
    id: "GRV-6003",
    facilityCcn: "445457",
    facilityName: "Monroe Health",
    complainantType: "resident",
    residentName: "Adelaide Ross",
    dateReceived: "2026-01-06",
    category: "dietary",
    department: "dietary",
    assignedTo: "Felicia Barnes, NHA",
    status: "closed",
    investigationNotes:
      "Resident reports her food allergy was not honored on a tray. Allergy flag verified in the system; a tray-line labeling lapse identified.",
    resolutionDate: "2026-01-10",
    outcome: "Allergy alert labels added to tray tickets; dietary staff re-educated and a double-check added at tray-line.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-01-10",
    employeesMentioned: ["Diane Cho"],
    narrative:
      "Resident Adelaide Ross states she was served a dish containing an ingredient she is allergic to and is fearful about meal safety going forward.",
  },

  // ── St. Jude Skilled Nursing (Demo) 999012 ─────────────────────────────────
  // The always-on sample facility (admin/VP). Seeded so every grievance report
  // is fully populated for demos/training: all statuses, a couple notification
  // gaps for the F585 tile, and a varied days-to-close spread.
  {
    id: "GRV-9001",
    facilityCcn: "999012",
    facilityName: "St. Jude Skilled Nursing (Demo)",
    complainantType: "family",
    residentName: "Harold Simmons",
    dateReceived: "2026-02-03",
    category: "care",
    department: "nursing",
    assignedTo: "Gloria Mendez, NHA",
    status: "resolved",
    investigationNotes:
      "Son reported his father's morning medications were administered late on three consecutive days. MAR reviewed; a shift-change handoff gap on the 300 hall was identified during the 6:00 pass.",
    resolutionDate: "2026-02-07",
    outcome:
      "Med-pass start time moved ahead 30 minutes on the 300 hall; charge nurse now verifies the 6:00 pass is complete before handoff. Family updated by phone.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-02-07",
    employeesMentioned: ["Angela Pruitt", "Patrick Doyle"],
    narrative:
      "Family of resident Harold Simmons reports his 6 a.m. medications were consistently given more than an hour late, causing him discomfort. Requests medications on schedule.",
  },
  {
    id: "GRV-9002",
    facilityCcn: "999012",
    facilityName: "St. Jude Skilled Nursing (Demo)",
    complainantType: "resident",
    residentName: "Doris Vance",
    dateReceived: "2026-03-19",
    category: "environment",
    department: "housekeeping",
    assignedTo: "Howard Quinn, NHA",
    status: "closed",
    investigationNotes:
      "Resident reports her room was not cleaned for several days and the bathroom had a persistent odor. Housekeeping logs showed two missed rooms on the 100 hall during a call-out.",
    resolutionDate: "2026-03-31",
    outcome:
      "Coverage plan updated for housekeeping call-outs; supervisor now spot-checks the daily cleaning log. Deep-clean completed for the affected rooms.",
    residentNotificationSent: null, // RESERVED gap — feed lacked notification flag
    residentNotificationDate: null,
    employeesMentioned: ["Sandra Kim"],
    narrative:
      "Resident Doris Vance states her room went uncleaned for most of a week and the bathroom smelled strongly, which she found embarrassing when family visited.",
  },
  {
    id: "GRV-9003",
    facilityCcn: "999012",
    facilityName: "St. Jude Skilled Nursing (Demo)",
    complainantType: "representative",
    residentName: "Walter Freeman",
    dateReceived: "2026-04-22",
    category: "rights",
    department: "social_services",
    assignedTo: "Renee Tucker, RN",
    status: "in_progress",
    investigationNotes:
      "Legal representative alleges the resident's care-plan meeting was held without notifying the responsible party. Reviewing meeting-invitation records and the resident's contact preferences.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Maria Delgado"],
    narrative:
      "Representative for resident Walter Freeman states they were not invited to the quarterly care-plan conference despite being the designated decision-maker, and want to participate going forward.",
  },
  {
    id: "GRV-9004",
    facilityCcn: "999012",
    facilityName: "St. Jude Skilled Nursing (Demo)",
    complainantType: "family",
    residentName: "Frances Koch",
    dateReceived: "2026-05-11",
    category: "dietary",
    department: "dietary",
    assignedTo: "Gloria Mendez, NHA",
    status: "resolved",
    investigationNotes:
      "Daughter reports the resident's pureed-diet order was not consistently followed at dinner. Tray tickets audited; two dinner trays over the week were plated at a regular texture.",
    resolutionDate: "2026-05-15",
    outcome:
      "Diet-texture flags reprinted on tray tickets; tray-line double-check added for all texture-modified orders. Family notified of corrective steps.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-05-15",
    employeesMentioned: ["Derek Olsen"],
    narrative:
      "Family of resident Frances Koch, who is on a pureed diet for swallowing safety, reports she was served regular-texture food at dinner and is worried about choking risk.",
  },
  {
    id: "GRV-9005",
    facilityCcn: "999012",
    facilityName: "St. Jude Skilled Nursing (Demo)",
    complainantType: "resident",
    residentName: "Stanley Boone",
    dateReceived: "2026-06-02",
    category: "staff_conduct",
    department: "nursing",
    assignedTo: "Angela Pruitt, RN",
    status: "open",
    investigationNotes:
      "Resident reports a night-shift aide spoke to him dismissively when he asked for help repositioning. Interviews with the assigned aide and the charge nurse are being scheduled.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: ["Patrick Doyle"],
    narrative:
      "Resident Stanley Boone states a night aide was curt and dismissive when he requested help turning in bed, leaving him feeling disrespected. Requests to be treated courteously.",
  },
  {
    id: "GRV-9006",
    facilityCcn: "999012",
    facilityName: "St. Jude Skilled Nursing (Demo)",
    complainantType: "family",
    residentName: "Mabel Pierce",
    dateReceived: "2026-01-27",
    category: "billing",
    department: "administration",
    assignedTo: "Howard Quinn, NHA",
    status: "closed",
    investigationNotes:
      "Son disputes a personal-needs-account charge he says was never authorized. Reviewed the trust-fund ledger and receipts; a beauty-shop charge was posted to the wrong resident account.",
    resolutionDate: "2026-02-04",
    outcome:
      "Charge reversed and the ledger corrected; trust-fund posting now requires a second verification against the service sign-in sheet. Family sent an itemized statement.",
    residentNotificationSent: true,
    residentNotificationDate: "2026-02-04",
    employeesMentioned: ["Karen Whitfield"],
    narrative:
      "Family of resident Mabel Pierce disputes a charge on her personal-needs account they say was never authorized and requests a corrected statement.",
  },
  {
    id: "GRV-9007",
    facilityCcn: "999012",
    facilityName: "St. Jude Skilled Nursing (Demo)",
    complainantType: "anonymous",
    residentName: null,
    dateReceived: "2026-06-14",
    category: "environment",
    department: "administration",
    assignedTo: "Gloria Mendez, NHA",
    status: "in_progress",
    investigationNotes:
      "Anonymous concern submitted about the temperature in the west day room being uncomfortably warm in the afternoons. Maintenance is checking the HVAC zone and thermostat calibration.",
    resolutionDate: null,
    outcome: null,
    residentNotificationSent: null,
    residentNotificationDate: null,
    employeesMentioned: [],
    narrative:
      "Anonymous complaint reports the west day room becomes uncomfortably hot in the afternoon, making it hard for residents to enjoy activities there.",
  },

  // ── St. Margaret's Care Center 999101 ──────────────────────────────────────
  // Deliberately shaped so the F585 dashboard has something to say about this
  // building specifically: one closed grievance with the resident NEVER
  // notified (the notification gap), one open past the 5-day flag, and one
  // resolved cleanly so the comparison is visible rather than uniform.
  {
    id: "GRV-9101", facilityCcn: "999101", facilityName: "St. Margaret's Care Center",
    complainantType: "family", residentName: "Harriet Pemberton",
    dateReceived: "2026-08-28", category: "care", department: "nursing",
    assignedTo: "Denise Okafor, RN — Grievance Official", status: "closed",
    investigationNotes:
      "Daughter reported that her mother's morning medications were given late on three occasions. Reviewed the MAR for a two-week period and interviewed both day-shift nurses. Two late administrations confirmed, both on days the unit was covered by agency staff.",
    resolutionDate: "2026-09-02",
    outcome:
      "Agency orientation checklist updated to include the medication pass window. Unit manager now verifies the 08:00 pass is complete by 09:30 on agency-covered days.",
    residentNotificationSent: false, residentNotificationDate: null,
    employeesMentioned: ["Agency staff — unnamed"],
    narrative:
      "Family states morning medications have been given late more than once and is concerned this is affecting her mother's blood pressure control.",
  },
  {
    id: "GRV-9102", facilityCcn: "999101", facilityName: "St. Margaret's Care Center",
    complainantType: "resident", residentName: "Cornelius Ibarra",
    dateReceived: "2026-09-08", category: "dietary", department: "dietary",
    assignedTo: "Denise Okafor, RN — Grievance Official", status: "in_progress",
    investigationNotes:
      "Resident reports his therapeutic diet trays arrive cold. Dietary manager is timing the tray line to the unit and checking the transport cart heating element.",
    resolutionDate: null, outcome: null,
    residentNotificationSent: null, residentNotificationDate: null,
    employeesMentioned: [],
    narrative:
      "Resident says his meals arrive cold most days and he has started skipping lunch because of it.",
  },
  {
    id: "GRV-9103", facilityCcn: "999101", facilityName: "St. Margaret's Care Center",
    complainantType: "representative", residentName: "Alma Rasmussen",
    dateReceived: "2026-09-12", category: "staff_conduct", department: "nursing",
    assignedTo: "Gloria Mendez, NHA", status: "resolved",
    investigationNotes:
      "Representative reported a CNA spoke sharply to the resident during a transfer. Interviewed the resident, the CNA and one witness. The CNA acknowledged raising her voice while managing an unsafe transfer alone.",
    resolutionDate: "2026-09-15",
    outcome:
      "Counselling documented. Two-person transfer added to the care plan. Dignity-in-care refresher scheduled for the unit.",
    residentNotificationSent: true, residentNotificationDate: "2026-09-15",
    employeesMentioned: ["Tonya Reeves"],
    narrative:
      "Representative reports the resident was spoken to sharply during a transfer and was upset afterwards.",
  },
  // ── St. Mary's Skilled Nursing 999102 ──────────────────────────────────────
  // A cleaner building than St. Margaret's on purpose: switching between the two
  // has to visibly change the dashboard, or the facility picker looks decorative.
  {
    id: "GRV-9201", facilityCcn: "999102", facilityName: "St. Mary's Skilled Nursing",
    complainantType: "family", residentName: "Solomon Whitmore",
    dateReceived: "2026-09-03", category: "environment", department: "housekeeping",
    assignedTo: "Priya Raghunathan, LNHA — Grievance Official", status: "closed",
    investigationNotes:
      "Family reported the resident's room was not cleaned over a weekend. Reviewed housekeeping assignment sheets; the room was missed on both weekend days due to an uncovered call-out.",
    resolutionDate: "2026-09-05",
    outcome:
      "Weekend assignment sheets now require a supervisor sign-off confirming every room is covered before shift end.",
    residentNotificationSent: true, residentNotificationDate: "2026-09-05",
    employeesMentioned: [],
    narrative:
      "Family arrived Sunday to find the room had not been cleaned since Friday.",
  },
  {
    id: "GRV-9202", facilityCcn: "999102", facilityName: "St. Mary's Skilled Nursing",
    complainantType: "anonymous", residentName: null,
    dateReceived: "2026-09-16", category: "care", department: "nursing",
    assignedTo: "Priya Raghunathan, LNHA — Grievance Official", status: "open",
    investigationNotes:
      "Anonymous concern about call-light response times on the evening shift. Pulling the call-light log for the prior two weeks.",
    resolutionDate: null, outcome: null,
    residentNotificationSent: null, residentNotificationDate: null,
    employeesMentioned: [],
    narrative:
      "Anonymous report that call lights on the evening shift go unanswered for long periods.",
  },
];
